/*
 * Formulaires : amélioration progressive. Sans JavaScript, tout fonctionne en envoi classique
 * (validation serveur, réaffichage des erreurs). Avec JavaScript : étapes, validation immédiate,
 * envoi sans rechargement et protection contre le double clic.
 */
const rules = {
  email: (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) || 'Vérifiez l’adresse e-mail, par exemple prenom@exemple.fr.',
  phone: (v) => {
    const d = v.replace(/[^\d+]/g, '');
    return /^(?:(?:\+|00)33|0)[1-9]\d{8}$/.test(d) || /^(?:\+|00)[1-9]\d{7,14}$/.test(d) || 'Vérifiez le numéro, par exemple 06 12 34 56 78.';
  },
  int: (v, el) => {
    const n = Number(v);
    const min = Number(el.dataset.min || 1950);
    const max = Number(el.dataset.max || new Date().getFullYear() + 1);
    return (/^\d+$/.test(v) && n >= min && n <= max) || `Indiquez une année entre ${min} et ${max}.`;
  },
  km: (v) => /^\d{1,7}$/.test(v.replace(/[\s.  ]/g, '')) || 'Indiquez un nombre, sans lettres, par exemple 85 000.',
  money: (v) => /^\d{1,7}$/.test(v.replace(/[\s.  ]/g, '')) || 'Indiquez un nombre, sans lettres, par exemple 15 000.',
};

function labelOf(field) {
  const l = field.querySelector('.field__label');
  return (l?.childNodes[0]?.textContent || '').trim();
}

function validateField(wrap) {
  const input = wrap.querySelector('input:not([type=radio]), select, textarea');
  const err = wrap.querySelector('.field__error');
  if (!input || !err) return true;
  const v = input.value.trim();
  let msg = '';
  if (!v && input.required) {
    msg = input.tagName === 'SELECT' ? 'Choisissez une option de la liste.' : `Ce champ est nécessaire : ${labelOf(wrap).toLowerCase()}.`;
  } else if (v && rules[input.dataset.rule]) {
    const r = rules[input.dataset.rule](v, input);
    if (r !== true) msg = r;
  }
  err.textContent = msg;
  wrap.classList.toggle('has-error', !!msg);
  if (msg) input.setAttribute('aria-invalid', 'true');
  else input.removeAttribute('aria-invalid');
  return !msg;
}

function showServerErrors(form, errors) {
  let first = null;
  Object.entries(errors || {}).forEach(([name, msg]) => {
    const wrap = form.querySelector(`[data-field="${CSS.escape(name)}"]`);
    if (!wrap) return;
    wrap.classList.add('has-error');
    const err = wrap.querySelector('.field__error');
    if (err) err.textContent = msg;
    wrap.querySelector('input, select, textarea')?.setAttribute('aria-invalid', 'true');
    first ??= wrap;
  });
  return first;
}

function setStatus(form, type, message) {
  const status = form.querySelector('[data-form-status]');
  status.hidden = false;
  status.className = `form-status is-${type}`;
  status.innerHTML = '';
  const p = document.createElement('p');
  p.className = 'form-status__msg';
  p.textContent = message;
  status.append(p);
  status.focus({ preventScroll: true });
  status.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
}

function initSteps(form) {
  const steps = [...form.querySelectorAll('.lead-form__step')];
  if (steps.length < 2) return null;
  const nav = form.querySelector('[data-steps-nav]');
  const dots = [...form.querySelectorAll('[data-step-dot]')];
  const submit = form.querySelector('.lead-form__submit');
  form.classList.add('lead-form--js');
  nav.hidden = false;
  form.querySelectorAll('[data-step-actions]').forEach((a) => { a.hidden = false; });
  let current = 0;

  const go = (i, focus = true) => {
    current = i;
    steps.forEach((s, k) => s.classList.toggle('is-current', k === i));
    dots.forEach((d, k) => {
      d.classList.toggle('is-current', k === i);
      d.classList.toggle('is-done', k < i);
      if (k === i) d.setAttribute('aria-current', 'step'); else d.removeAttribute('aria-current');
    });
    submit.hidden = i !== steps.length - 1;
    if (focus) steps[i].querySelector('input:not([type=radio]), select, textarea, input[type=radio]:checked')?.focus();
  };

  form.addEventListener('click', (e) => {
    if (e.target.closest('[data-next]')) {
      const fields = [...steps[current].querySelectorAll('.field')];
      const ok = fields.map(validateField).every(Boolean);
      if (!ok) {
        steps[current].querySelector('.has-error input, .has-error select, .has-error textarea')?.focus();
        return;
      }
      go(current + 1);
      form.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    if (e.target.closest('[data-prev]')) go(current - 1);
  });

  // Si le serveur a renvoyé des erreurs, ouvrir l'étape concernée.
  const firstError = steps.findIndex((s) => s.querySelector('.has-error'));
  go(firstError >= 0 ? firstError : 0, false);
  return { go, indexOf: (el) => steps.findIndex((s) => s.contains(el)) };
}

export function initForms(track) {
  document.querySelectorAll('form[data-form]').forEach((form) => {
    const name = form.dataset.form;
    const stepper = initSteps(form);
    const btn = form.querySelector('[data-submit]');
    const label = form.querySelector('[data-submit-label]');
    const tokenInput = form.querySelector('input[name=_token]');
    const loadedAt = Date.now();
    let busy = false;

    form.addEventListener('focusin', () => track(`debut_${name}`, true), { once: true });
    form.addEventListener('focusout', (e) => {
      const wrap = e.target.closest('.field');
      if (wrap && e.target.value !== '') validateField(wrap);
    });
    form.addEventListener('input', (e) => {
      const wrap = e.target.closest('.field.has-error');
      if (wrap) validateField(wrap);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (busy) return;
      const wraps = [...form.querySelectorAll('.field')];
      const valid = wraps.map(validateField).every(Boolean);
      if (!valid) {
        const bad = form.querySelector('.has-error');
        if (stepper) stepper.go(stepper.indexOf(bad), false);
        bad?.querySelector('input, select, textarea')?.focus();
        return;
      }
      busy = true;
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      const original = label.textContent;
      label.textContent = 'Envoi en cours…';

      try {
        // Formulaire resté ouvert très longtemps : on renouvelle le jeton avant l'envoi.
        if (Date.now() - loadedAt > 110 * 60 * 1000) {
          const t = await fetch(`/jeton?f=${encodeURIComponent(name)}`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
          if (t.token) tokenInput.value = t.token;
        }
        const res = await fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const data = await res.json().catch(() => ({ status: 'error', message: 'Réponse inattendue du serveur. Réessayez dans un instant.' }));
        if (data.token) tokenInput.value = data.token;
        if (data.status === 'ok') {
          form.classList.add('is-sent');
          setStatus(form, 'ok', data.message);
          window.dispatchEvent(new CustomEvent('jg:lead', { detail: { form: name } }));
          return;
        }
        const first = showServerErrors(form, data.errors);
        setStatus(form, 'error', data.message || 'L’envoi n’a pas abouti. Réessayez.');
        if (first && stepper) stepper.go(stepper.indexOf(first), false);
      } catch {
        setStatus(form, 'error', 'Connexion impossible. Vérifiez votre réseau puis renvoyez le formulaire : vos informations sont conservées.');
      } finally {
        busy = false;
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        label.textContent = original;
      }
    });
  });
}
